import React from "react";
import { Stethoscope, Activity, MessageCircle, AlertTriangle } from "lucide-react";
import FOHGuidancePanel from "./FOHGuidancePanel";
import WarningLightFaultTree from "./WarningLightFaultTree";
import NoiseVibrationFaultTree from "./NoiseVibrationFaultTree";
import IntermittentFaultTree from "./IntermittentFaultTree";
import BrakesFaultTree from "./BrakesFaultTree";
import PerformanceFaultTree from "./PerformanceFaultTree";

/**
 * Diagnostic Companion — section skeleton for the Diagnostic path.
 *
 * Currently ships ONE working fault-tree template (Warning Light, deep).
 * Future Phase 2 templates (Noise / Performance / Intermittent / Brakes)
 * appear in the picker below as disabled placeholders so the FOH already
 * sees the shape of the full library.
 */
export default function DiagnosticCompanion({ data = {}, onChange = () => {} }) {
  const template = data.template || "warning_light";

  const setTemplate = (t) => {
    // Switching template clears template-specific capture state
    onChange({ template: t, fault_tree: {} });
  };

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4"
      data-testid="diagnostic-companion"
    >
      <div className="space-y-3">
        <CompanionHeader
          tone="amber"
          icon={<Stethoscope className="w-4 h-4" />}
          eyebrow="Reason · Diagnostic"
          title="Diagnostic intake"
          subtitle="Pick the closest concern then walk the customer through structured triage."
        />

        <Section
          icon={<Activity className="w-3.5 h-3.5" />}
          label="Concern template"
          testId="diagnostic-companion-template"
        >
          <p className="text-[13px] text-slate-500 leading-snug mb-2.5">
            Choose the fault-tree that fits best — keeps capture structured so
            the technician opens the bay with real intelligence.
          </p>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((t) => {
              const isOn = template === t.id;
              const disabled = !t.available;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && setTemplate(t.id)}
                  className={`px-3.5 py-2 rounded-md border text-[13.5px] font-medium transition-colors flex items-center gap-2 ${
                    isOn
                      ? "border-rose-500 bg-rose-50 text-rose-800"
                      : disabled
                      ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                  data-testid={`diag-template-${t.id}`}
                  title={disabled ? "Coming in Phase 2" : t.label}
                >
                  {t.label}
                  {disabled && (
                    <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Active fault tree */}
        {template === "warning_light" && (
          <WarningLightFaultTree
            data={data.fault_tree || {}}
            onChange={(patch) =>
              onChange({ fault_tree: { ...(data.fault_tree || {}), ...patch } })
            }
            onReset={() => onChange({ fault_tree: {} })}
          />
        )}
        {template === "noise_vibration" && (
          <NoiseVibrationFaultTree
            data={data.fault_tree || {}}
            onChange={(patch) =>
              onChange({ fault_tree: { ...(data.fault_tree || {}), ...patch } })
            }
            onReset={() => onChange({ fault_tree: {} })}
          />
        )}
        {template === "intermittent" && (
          <IntermittentFaultTree
            data={data.fault_tree || {}}
            onChange={(patch) =>
              onChange({ fault_tree: { ...(data.fault_tree || {}), ...patch } })
            }
            onReset={() => onChange({ fault_tree: {} })}
          />
        )}
        {template === "brakes" && (
          <BrakesFaultTree
            data={data.fault_tree || {}}
            onChange={(patch) =>
              onChange({ fault_tree: { ...(data.fault_tree || {}), ...patch } })
            }
            onReset={() => onChange({ fault_tree: {} })}
          />
        )}
        {template === "performance" && (
          <PerformanceFaultTree
            data={data.fault_tree || {}}
            onChange={(patch) =>
              onChange({ fault_tree: { ...(data.fault_tree || {}), ...patch } })
            }
            onReset={() => onChange({ fault_tree: {} })}
          />
        )}

        {/* Free-text concern — always available */}
        <Section
          icon={<MessageCircle className="w-3.5 h-3.5" />}
          label="Customer’s own words"
          testId="diagnostic-companion-concern"
        >
          <textarea
            rows={2}
            placeholder="Capture verbatim — ‘feels juddery when I brake from motorway speed’ lands sharper than a paraphrase."
            value={data.customer_concern || ""}
            onChange={(e) => onChange({ customer_concern: e.target.value })}
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-rose-500"
            data-testid="diag-customer-concern"
          />
        </Section>
      </div>

      <FOHGuidancePanel
        tone="amber"
        title="Diagnostic guidance"
        askNext={[
          "Anchor the symptom in the customer’s own words first — write it down verbatim.",
          "Then walk the fault tree end-to-end before quoting time.",
        ]}
        sayToCustomer={[
          "Diagnostics is staged testing — we book the first stage so the technician can build a working hypothesis before committing to repair time.",
          "We always call you back with what we've found before any extra work goes ahead.",
        ]}
        watchOuts={[
          "If lamp is RED + steady/flashing, advise customer not to drive — arrange recovery.",
          "Avoid quoting a fix on the phone — diagnostic outcomes shape the repair, not the other way around.",
        ]}
      />
    </div>
  );
}

const TEMPLATES = [
  { id: "warning_light", label: "Warning light", available: true },
  { id: "noise_vibration", label: "Noise / Vibration", available: true },
  { id: "intermittent", label: "Intermittent fault", available: true },
  { id: "brakes", label: "Brakes / Pedal feel", available: true },
  { id: "performance", label: "Performance / Power", available: true },
];

// ---------------------------------------------------------------------------

function CompanionHeader({ tone, icon, eyebrow, title, subtitle }) {
  const bg = TONE_HEADER[tone] || TONE_HEADER.amber;
  return (
    <div className={`relative overflow-hidden rounded-lg px-4 py-3 text-white ${bg}`}>
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_white_0%,_transparent_55%)]" />
      <div className="relative flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-md bg-white/20 border border-white/30 flex items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold opacity-80">
            {eyebrow}
          </p>
          <h3 className="text-[17px] font-bold leading-tight truncate">{title}</h3>
          {subtitle && (
            <p className="text-[12.5px] opacity-90 leading-snug mt-0.5 max-w-[60ch]">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon, label, testId, children }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <header className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-slate-100">
        <span className="text-slate-500">{icon}</span>
        <p className="text-[11.5px] uppercase tracking-[0.18em] text-slate-700 font-semibold">
          {label}
        </p>
      </header>
      <div className="px-4 py-3.5">{children}</div>
    </section>
  );
}

const TONE_HEADER = {
  blue: "bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700",
  amber: "bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600",
  teal: "bg-gradient-to-r from-teal-600 via-teal-700 to-emerald-700",
};
